import { useEffect, useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { COMMUNICATION_CATEGORIES } from './categories';
import { CategoryAddDropdown } from './CategoryAddDropdown';
import { CategoryChip } from './CategoryChip';

interface CategorySelectorProps {
    categories: string[];
    onChange: (next: string[]) => void;
    disabled: boolean;
}

// Selected categories render as removable pills; a dashed "+ Add a
// category" trigger opens a searchable, multi-select menu with checkboxes -
// picks toggle membership without closing the menu (see CategoryAddDropdown).
export function CategorySelector({ categories, onChange, disabled }: CategorySelectorProps) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const addRef = useRef<HTMLButtonElement>(null);
    const allSelected = categories.length >= COMMUNICATION_CATEGORIES.length;

    // Click-outside is owned by CategoryAddDropdown's FloatingMenu (it's
    // portaled, so this component can't rely on a DOM ancestor check) -
    // Escape still needs its own listener since FloatingMenu doesn't bind one.
    useEffect(() => {
        if (!dropdownOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setDropdownOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [dropdownOpen]);

    const handleToggle = (categoryId: string) => {
        onChange(
            categories.includes(categoryId)
                ? categories.filter((c) => c !== categoryId)
                : [...categories, categoryId]
        );
    };

    const handleRemove = (categoryId: string) => {
        onChange(categories.filter((c) => c !== categoryId));
    };

    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {categories.map((id) => (
                <CategoryChip key={id} id={id} onRemove={() => handleRemove(id)} />
            ))}
            {!allSelected && (
                <>
                    <button
                        ref={addRef}
                        type="button"
                        disabled={disabled}
                        onClick={() => setDropdownOpen((v) => !v)}
                        className={`inline-flex items-center gap-1 rounded-full border border-dashed px-2.5 h-7 text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                            dropdownOpen
                                ? 'border-gray-800 text-gray-900 bg-gray-50'
                                : 'border-gray-300 text-gray-500 hover:border-gray-500'
                        }`}
                    >
                        <PlusIcon className="h-3.5 w-3.5" />
                        {categories.length > 0 ? 'Category' : 'Add a category'}
                    </button>
                    {dropdownOpen && (
                        <CategoryAddDropdown
                            triggerRef={addRef}
                            selected={categories}
                            onToggle={handleToggle}
                            onClose={() => setDropdownOpen(false)}
                        />
                    )}
                </>
            )}
        </div>
    );
}
